import React, { useState, useEffect, useRef, useCallback } from 'react'

import { useModal } from '../../../providers/ModalProvider/ModalProvider'
import { usePagination } from '../../../providers/PaginationProvider/PaginationProvider'

import { useTrackApiCalls } from '../../../utilities/useTrackApiCalls'
import useDebouncedValue from '../../../utilities/useDebouncedValue'
import useAbortableFetch from '../../../utilities/useAbortableFetch'

import { getPackages, removePackage } from '../../../utilities/api'
import AuthService from '../../../utilities/services/auth.service'

import { globalStatusOpts } from '../../../utilities/helpers'

import Card from 'react-bootstrap/Card'
import Stack from 'react-bootstrap/Stack'
import Form from 'react-bootstrap/Form'
import FloatingLabel from 'react-bootstrap/FloatingLabel'

import { SearchBar } from '../../SearchBar'
import { Package } from './Package'
import { Delete } from '../../ModalContent'
import { LoadingContainer } from '../../LoadingContainer'
import { Pagination } from '../../Pagination'

export default function PackagesList({ seasonId }) {
  // to track if component has mounted once already
  const didMountRef = useRef(false)
  // to track if seasons have changed and to reset state
  const isSeasonResetRef = useRef(false)
  // to track how much state should be reset
  const resetTimesRef = useRef(0)
  const resetCountRef = useRef(0)

  const { incrementCallId, isLastCall } = useTrackApiCalls()

  const { openModal, closeModal } = useModal()

  const [status, setStatus] = useState('upcoming')

  // state to track if component has mounted once already
  const [isFirstTime, setIsFirstTime] = useState(true)

  // initial render loading
  const [isLoadingInitial, setIsLoadingInitial] = useState(false)

  // set when searching or switching statuses - show spinner
  const [isLoading, setIsLoading] = useState(false)

  // search query
  const [query, setQuery] = useState('')

  const [eventPackages, setEventPackages] = useState([])

  // total of all packages
  const [total, setTotal] = useState()

  // Hooks for convenience
  const debouncedQuery = useDebouncedValue(query, 300)
  const abortable = useAbortableFetch()

  const {
    currentPage,
    setCurrentPage,
    perPage,
    pageCount,
    setPageCount,
    resultsPerPageOpts,
    handleChange,
  } = usePagination()

  const runSearch = useCallback(
    async (isFirstTime, q, s, callId) => {
      console.log('run search', isFirstTime, seasonId)
      if (isFirstTime) setIsLoadingInitial(true)
      if (!isLoading) setIsLoading(true)
      try {
        const res = await abortable((signal) =>
          getPackages(
            {
              seasonId: Number(seasonId),
              status: s,
              query: q,
              page: currentPage + 1,
              perPage,
              organizationUUID: AuthService.getOrg()?.[0]?.uuid,
            },
            signal
          )
        )

        const {
          data,
          meta: {
            pagination: { pageCount: pc, allPackageTotal: total },
          },
        } = res.data

        // if on last api call set state
        if (isLastCall(callId)) {
          setEventPackages(data)
          console.log('is last cal', total)
          setTotal(total)
          setPageCount(pc)
          setIsLoading(false)
        }
      } catch (err) {
        if (err?.name !== 'CanceledError') {
          console.error(err)
          setIsLoading(false)
        }
      } finally {
        if (isFirstTime) setIsLoadingInitial(false)
        if (isLastCall(callId)) {
          setIsFirstTime(isFirstTime)
          setIsLoading(false)
        }
      }
    },
    [
      abortable,
      currentPage,
      perPage,
      debouncedQuery,
      status,
      setPageCount,
      seasonId,
    ]
  )

  // run when currentPage or perPage changes and is mounted
  useEffect(() => {
    if (!didMountRef.current) return // ⛔ skip on first render - to prevent duplicate runSearch calls
    callApi() // ✅ run only for real changes
  }, [currentPage, perPage])

  // run when query or status changes and currentPage is 0 and is mounted
  useEffect(() => {
    // Guard: If we are in the middle of a season reset, skip the API call flow
    // keep count of how much state (the most is 2) we've reset to not turn the guard off after the first one
    if (isSeasonResetRef.current) {
      resetCountRef.current += 1
      // if all finished resetting state, turn guard off
      if (resetCountRef.current === resetTimesRef.current)
        isSeasonResetRef.current = false
      return
    }

    if (currentPage === 0 && didMountRef.current) {
      callApi()
      return
    }

    setCurrentPage(0)
  }, [debouncedQuery, status])

  // Logic to reset query and status when seasonId changes
  useEffect(() => {
    // Skip on the very first mount
    if (!didMountRef.current) return

    resetCountRef.current = 0

    // 1. Set the flag to temporarily suppress the filter effect
    isSeasonResetRef.current = true

    // (1) no filters changed
    if (status === 'upcoming' && query === '') isSeasonResetRef.current = false
    // (2) query and status did not change
    else if (query && status === 'upcoming') {
      resetTimesRef.current = 1
      setQuery('')
    }
    // (3) query did not change but status did - won't be 'upcoming' because of (1)
    else if (!query && status) {
      resetTimesRef.current = 1
      setStatus('upcoming')
    }
    // (4) query and status changed - status is not 'upcoming' because of (2)
    else {
      resetTimesRef.current = 2
      setStatus('upcoming')
      setQuery('')
    }
    setCurrentPage(0) // Resetting the page ensures a fresh search from the start

    callApi(true, '', 'upcoming')
  }, [seasonId])

  useEffect(() => {
    callApi(true) // True as in it is initial query
    didMountRef.current = true
  }, [])

  const callApi = (isFirstTime = false, query = debouncedQuery, s = status) => {
    const callId = incrementCallId() // Increment for each new call
    runSearch(isFirstTime, query, s, callId)
  }

  const openDeleteModal = (_, id) => {
    openModal(
      Delete,
      {
        entity: 'package',
        handleDelete,
        id,
        onClose: closeModal,
        requireTypeToConfirm: true,
      },
      { className: '', contentClassName: '' }
    )
  }

  const handleDelete = (id) => {
    return new Promise((resolve, reject) => {
      removePackage(id)
        .then((res) => {
          setEventPackages(eventPackages?.filter((pk) => pk.id !== id))
          resolve(res)
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  }

  return (
    <>
      {isLoadingInitial ? (
        <LoadingContainer />
      ) : (
        <>
          <Card body className="card--md">
            <Header
              total={total}
              isFirstTime={isFirstTime}
              setQuery={setQuery}
              setStatus={setStatus}
              status={status}
            />
            <Results
              isLoading={isLoading}
              isFirstTime={isFirstTime}
              eventPackages={eventPackages}
              total={total}
              openDeleteModal={openDeleteModal}
            />
          </Card>
          {!isLoading && eventPackages?.length > 0 && (
            <Pagination
              pageCount={pageCount}
              perPage={perPage}
              currentPage={currentPage}
              resultsPerPageOpts={resultsPerPageOpts}
              handleClick={(value) => setCurrentPage(value.selected)}
              handleChange={handleChange}
            />
          )}
        </>
      )}
    </>
  )
}

function Header({ total, isFirstTime, setQuery, status, setStatus }) {
  return (
    <>
      {(!isFirstTime || total > 0) && (
        <>
          <div className="section-header-sm section-heading section-heading--primary">
            <Card.Title as="h1" className="card-title-lg">
              Season Packages
            </Card.Title>
          </div>
          <Stack direction="horizontal" gap={3} className="mb-5">
            <div className="flex-grow-1">
              <SearchBar getQuery={setQuery} placeholder="Search by name" />
            </div>
            <FloatingLabel controlId="status" label="Status">
              <Form.Select
                name="status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {globalStatusOpts.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Stack>
        </>
      )}
    </>
  )
}

function Results({
  isLoading,
  eventPackages,
  isFirstTime,
  total,
  openDeleteModal,
}) {
  if (isLoading) {
    return <LoadingContainer />
  }

  // first time render and no packages - won't trigger when searching
  if (isFirstTime && total === 0) {
    return (
      <>
        <h1 className="fs-md text-center">No season packages.</h1>
        <p className="small text-center">
          Create a package and start selling tickets.
        </p>
      </>
    )
  }

  if (eventPackages?.length === 0) {
    return (
      <>
        <h1 className="fs-md text-center">
          No season packages matching filters.
        </h1>
        <p className="small text-center">
          Please adjust your search filters and try again.
        </p>
      </>
    )
  }

  return (
    <Card body className="card--sm card--dark">
      <Stack as="ul" gap={3} id="flex-packs-list">
        {eventPackages.map((eventPackage, index) => (
          <li key={index}>
            <Package
              key={index}
              eventPackage={eventPackage}
              openDeleteModal={openDeleteModal}
            />
          </li>
        ))}
      </Stack>
    </Card>
  )
}
